/* RUVEÉ motion lower half v2: reveal, liquid bars + count-up, live icon loops,
   pairing price, pulse-point map, jump-chip spy. IntersectionObserver only, no scroll listeners. */
(() => {
  const roots = document.querySelectorAll('[data-rvm]');
  if (!roots.length) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hasIO = 'IntersectionObserver' in window;
  const ease = (t) => 1 - Math.pow(1 - t, 4); /* close match to cubic-bezier(.22,1,.36,1) */

  /* ---------- time axis: piecewise map of minutes to % (0, 20m, 1h, 4h, 8h+) ---------- */
  const AX = [[0, 0], [20, 14], [60, 26], [240, 58], [480, 100]];
  const toPct = (m) => {
    m = Math.max(0, Math.min(480, m));
    for (let i = 1; i < AX.length; i++) {
      const [a, pa] = AX[i - 1], [b, pb] = AX[i];
      if (m <= b) return pa + ((pb - pa) * (m - a)) / (b - a);
    }
    return 100;
  };
  const parseRange = (txt) => {
    const t = txt.toLowerCase();
    const re = /(\d+(?:\.\d+)?)\s*(minutes?|mins?|hours?|hrs?)?/g;
    const out = []; let m;
    while ((m = re.exec(t))) out.push({ n: parseFloat(m[1]), u: m[2] || null });
    if (!out.length) return null;
    let unit = null;
    for (let i = out.length - 1; i >= 0; i--) { if (out[i].u) unit = out[i].u; else out[i].u = unit; }
    const mins = out.map((o) => (o.u && o.u.charAt(0) === 'h' ? o.n * 60 : o.n));
    const open = /onward|beyond|\+|plus/.test(t) || mins.length === 1;
    const start = mins[0];
    const end = open ? 480 : Math.max(mins[1], start + 1);
    return [start, end];
  };

  /* ---------- count-up numbers inside a time label ---------- */
  const prepCount = (el) => {
    const txt = el.textContent.trim();
    if (!/\d/.test(txt)) return [];
    const sr = document.createElement('span'); sr.className = 'rvm-vh'; sr.textContent = txt;
    const vis = document.createElement('span'); vis.setAttribute('aria-hidden', 'true');
    let idx = 0; const nums = [];
    txt.replace(/\d+/g, (d, off) => {
      vis.append(txt.slice(idx, off));
      const s = document.createElement('span');
      s.className = 'rvm-num'; s.dataset.to = d; s.style.minWidth = d.length + 'ch'; s.textContent = '0';
      vis.append(s); nums.push(s); idx = off + d.length; return d;
    });
    vis.append(txt.slice(idx));
    el.textContent = ''; el.append(sr, vis);
    return nums;
  };
  const runCount = (nums, delay, dur) => {
    if (!nums.length) return;
    setTimeout(() => {
      const t0 = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - t0) / dur);
        nums.forEach((n) => { n.textContent = String(Math.round(Number(n.dataset.to) * ease(p))); });
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, delay);
  };

  roots.forEach((root) => {
    /* ---------- bars: place fills from parsed stage_time (fallback positions are server-rendered) ---------- */
    const barLists = root.querySelectorAll('[data-rvm-bars]');
    const barCounts = new Map();
    barLists.forEach((list) => {
      const rows = Array.from(list.querySelectorAll('.rvm-bar'));
      const perRow = rows.map((row) => {
        const time = row.querySelector('[data-rvm-time]');
        const fill = row.querySelector('[data-rvm-fill]');
        if (time && fill) {
          const r = parseRange(time.textContent);
          if (r) {
            const l = toPct(r[0]);
            const w = Math.max(3, toPct(r[1]) - l);
            fill.style.left = l + '%'; fill.style.width = w + '%';
          }
        }
        return (!reduce && hasIO && time) ? prepCount(time) : [];
      });
      barCounts.set(list, perRow);
    });

    /* ---------- jump chips ---------- */
    const nav = root.querySelector('.rvm-chips');
    const chipList = root.querySelector('.rvm-chips__list');
    const chips = Array.from(root.querySelectorAll('.rvm-chip'));
    const targets = new Map();
    chips.forEach((chip) => {
      const target = document.getElementById(decodeURIComponent(chip.hash.slice(1)));
      if (target) targets.set(target, chip); else chip.parentElement.hidden = true;
    });
    if (nav) {
      const setH = () => root.style.setProperty('--rvm-chip-h', nav.offsetHeight + 'px');
      setH();
      if ('ResizeObserver' in window) new ResizeObserver(setH).observe(nav);
    }

    /* ---------- pairing: price follows the chosen size ---------- */
    root.addEventListener('change', (e) => {
      const r = e.target;
      if (!r.matches || !r.matches('input[type="radio"][data-price]')) return;
      const card = r.closest('[data-rvm-card]');
      const price = card && card.querySelector('[data-rvm-price]');
      if (price) price.textContent = r.dataset.price;
    });

    /* ---------- pulse-point map legend ---------- */
    root.addEventListener('click', (e) => {
      const key = e.target.closest && e.target.closest('.rvm-map__key');
      if (!key) return;
      const map = key.closest('[data-rvm-map]');
      const turnOn = key.getAttribute('aria-pressed') !== 'true';
      map.querySelectorAll('.rvm-map__key').forEach((k) => k.setAttribute('aria-pressed', 'false'));
      map.querySelectorAll('.rvm-map__pt').forEach((p) => p.classList.remove('is-hot'));
      if (turnOn) {
        key.setAttribute('aria-pressed', 'true');
        map.querySelectorAll('.rvm-map__pt[data-pt="' + key.dataset.pt + '"]').forEach((p) => p.classList.add('is-hot'));
      }
    });

    if (!hasIO) return;

    if (!reduce) {
      root.classList.add('rvm-js');

      /* one-shot reveals */
      const once = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('is-in'); once.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
      root.querySelectorAll('.rvm-reveal, [data-rvm-once]').forEach((el) => once.observe(el));

      /* live containers: entrance once, idle loops only while visible */
      const live = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          const el = e.target;
          if (e.isIntersecting) {
            if (!el.classList.contains('is-in')) {
              el.classList.add('is-in');
              const rows = barCounts.get(el);
              if (rows) rows.forEach((nums, i) => runCount(nums, i * 100 + 150, 900));
            }
            el.classList.add('is-live');
          } else {
            el.classList.remove('is-live');
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.15 });
      root.querySelectorAll('[data-rvm-live]').forEach((el) => live.observe(el));
    }

    /* active chip */
    const spy = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const chip = targets.get(e.target);
        if (!chip) return;
        chips.forEach((c) => c.removeAttribute('aria-current'));
        chip.setAttribute('aria-current', 'location');
        if (chipList && chipList.scrollWidth > chipList.clientWidth) {
          chipList.scrollTo({ left: chip.offsetLeft - 16, behavior: reduce ? 'auto' : 'smooth' });
        }
      });
    }, { rootMargin: '-40% 0px -55% 0px' });
    targets.forEach((_, t) => spy.observe(t));
  });
})();
